import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { NgModel } from '@angular/forms'

@Component({
  selector: 'op-input-number-ng',
  templateUrl: './input-number-ng.component.html',
  styleUrls: ['./input-number-ng.component.scss'],
})
export class InputNumberNgComponent implements OnInit, OnDestroy {
  @ViewChild('inputModel', { static: true }) inputModel!: NgModel

  private readonly host = inject(ElementRef<HTMLElement>)
  private decimalAliasCleanup?: () => void

  @Input() placeholder?: string
  @Input() key?: string
  @Input() value: number | undefined | null
  @Input() useGrouping = true

  // Style
  @Input() clearButton = false
  @Input() disabled = false
  @Input() markIfValid = false
  @Input() height: '2.25rem' | '3rem' = '2.25rem'
  @Input() fontSize: '1rem' | '1.25rem' = '1rem'

  // Group management
  @Input() group = false
  @Input() preContent = false
  @Input() preLabel?: string
  @Input() postContent = false
  @Input() postLabel?: string

  // Errors
  @Input() required = false
  @Input() requiredError?: string
  @Input() invalidInput = false

  // Number specific inputs
  @Input() min?: number
  @Input() max?: number
  @Input() maxCharacters?: number
  @Input() step = 1
  @Input() disableDecimals = false

  @Output() readonly valueChange = new EventEmitter<number>()
  @Output() readonly valueSubmit = new EventEmitter<number>()
  @Output() readonly focusOut = new EventEmitter<number>()

  ngOnInit(): void {
    queueMicrotask(() => this.attachDecimalDotAsCommaAlias())
  }

  ngOnDestroy(): void {
    this.decimalAliasCleanup?.()
  }

  /**
   * Locale (e.g. it): comma is the decimal separator; PrimeNG rejects '.'.
   * Map '.' / numpad decimal to ',' before Prime handles the key (capture).
   */
  private attachDecimalDotAsCommaAlias(): void {
    this.decimalAliasCleanup?.()
    const el = this.host.nativeElement.querySelector(
      'input.p-inputnumber-input, .p-inputnumber input.p-inputtext',
    )
    if (!(el instanceof HTMLInputElement)) {
      return
    }
    const input = el
    const onKeydown = (event: KeyboardEvent) => {
      if (this.disabled || this.disableDecimals) {
        return
      }

      const isComma = event.key === ','
      const isPeriodOrNumpadDecimal =
        event.key === '.' ||
        event.key === 'Decimal' ||
        event.code === 'NumpadDecimal'

      if (!isComma && !isPeriodOrNumpadDecimal) {
        return
      }

      // Always prevent the native event — we'll manually dispatch if valid
      event.preventDefault()
      event.stopPropagation()

      const start = input.selectionStart ?? 0
      const end = input.selectionEnd ?? 0
      const insert = ','

      // Simulate the resulting string after inserting a comma
      const next = input.value.slice(0, start) + insert + input.value.slice(end)
      const commaCount = next.split(',').length - 1

      // Block if this would result in more than one comma
      if (commaCount > 1) {
        return
      }

      input.value = next
      const caret = start + insert.length
      input.setSelectionRange(caret, caret)

      input.dispatchEvent(
        new InputEvent('input', {
          bubbles: true,
          cancelable: true,
          data: insert,
          inputType: 'insertText',
        }),
      )
    }
    input.addEventListener('keydown', onKeydown, true)
    this.decimalAliasCleanup = () =>
      input.removeEventListener('keydown', onKeydown, true)
  }

  get sizeVars(): Record<string, string> {
    const vars: Record<string, string> = {}
    if (this.height) vars['--op-input-height'] = this.height
    if (this.fontSize) vars['--op-input-font-size'] = this.fontSize
    return vars
  }

  // Validation state properties
  get isValid(): boolean {
    return (
      this.markIfValid && !!(this.inputModel?.valid && this.inputModel?.touched)
    )
  }

  get isInvalid(): boolean {
    return (
      this.invalidInput ||
      !!(this.inputModel?.invalid && this.inputModel?.touched)
    )
  }

  clearValue() {
    this.value = 0
  }

  onValueChange(v: number) {
    const numericValue = v === null || isNaN(v) ? undefined : Number(v)
    this.value = numericValue
    this.valueChange.emit(numericValue)
  }

  increment() {
    const currentValue = this.value ?? 0
    const newValue = currentValue + this.step

    if (this.max !== undefined && newValue > this.max) {
      this.updateValue(this.max)
    } else {
      this.updateValue(newValue)
    }
  }

  decrement() {
    const currentValue = this.value ?? 0
    const newValue = currentValue - this.step

    if (this.min !== undefined && newValue < this.min) {
      this.updateValue(this.min)
    } else {
      this.updateValue(newValue)
    }
  }

  private updateValue(newValue: number) {
    this.value = newValue
    this.valueChange.emit(newValue)
    // Update the ngModel to reflect the change
    if (this.inputModel?.control) {
      this.inputModel.control.setValue(newValue, { emitEvent: false })
    }
  }

  isMinReached(): boolean {
    if (this.min === undefined) {
      return false
    }
    return (this.value ?? 0) <= this.min
  }

  isMaxReached(): boolean {
    if (this.max === undefined) {
      return false
    }
    return (this.value ?? 0) >= this.max
  }
}
